import { Test } from './test'
import { now } from './stats'
//
//
//

export const replaceSdp = (sdp: RTCSessionDescriptionInit, newSdp: string) => {
  return new RTCSessionDescription({
    type: sdp.type,
    sdp: newSdp,
  })
}

export const filterConfig = (config: any, protocol: string) => {
  const transport = 'transport=' + protocol
  for (let i = 0; i < config.iceServers.length; ++i) {
    const iceServer = config.iceServers[i]
    const newUrls = []
    for (let j = 0; j < iceServer.urls.length; ++j) {
      const uri = iceServer.urls[j]
      if (uri.indexOf(transport) !== -1) {
        newUrls.push(uri)
      } else if (uri.indexOf('?transport=') === -1 && uri.startsWith('turn')) {
        newUrls.push(uri + '?' + transport)
      }
    }
    iceServer.urls = newUrls
  }
}

type RTCStatsReportEntry =
  | RTCIceCandidatePairStats
  | RTCInboundRtpStreamStats
  | RTCOutboundRtpStreamStats

export const setTimeoutWithProgressBar = (
  test: Test,
  timeoutMs: number,
  timeoutCallback: () => void,
) => {
  const start = now()
  const updateProgressBar = setInterval(() => {
    test.setProgress(((now() - start) * 100) / timeoutMs)
  }, 1000)
  const timeoutTask = () => {
    if (test.state === 'running') {
      clearInterval(updateProgressBar)
      test.setProgress(100)
      timeoutCallback()
    }
  }
  const timer = setTimeout(timeoutTask, timeoutMs)
  const finishProgressBar = () => {
    clearTimeout(timer)
    timeoutTask()
  }
  return finishProgressBar
}

// TODO why ?
function noop() {}

// Create a PeerConnection, and gather candidates using RTCConfig |config|
// and ctor params |params|. Succeed if any candidates pass the |isGood|
// check, fail if we complete gathering without any passing.
export const gatherCandidates = (
  test: Test,
  config: any,
  params: any,
  isGood: (candicate: any) => boolean,
) => {
  test.reportInfo(
    'Use IceServers ' + JSON.stringify(config?.iceServers[0]?.urls),
  )

  let pc: RTCPeerConnection
  try {
    pc = Call.openPeerConnection(config, params)
  } catch (error) {
    return test.reportFatal(error as Error)
  }

  // In our candidate callback, stop if we get a candidate that passes |isGood|.
  pc.onicecandidate = function (e) {
    // Once we've decided, ignore future callbacks.
    if (pc.signalingState === 'closed') {
      return
    }

    if (e.candidate) {
      const parsed = Call.parseCandidate(e.candidate.candidate)
      if (isGood(parsed)) {
        test.reportSuccess(
          'Gathered candidate with type: ' +
            parsed.type +
            ' protocol: ' +
            parsed.protocol +
            ' address: ' +
            parsed.address,
        )
        pc.close()
        test.done()
      }
    } else {
      pc.close()
      test.reportFatal('Failed to gather specified candidates')
    }
  }

  // Create an audio-only, recvonly offer, and setLD with it.
  // This will trigger candidate gathering.
  const createOfferParams: RTCOfferOptions = {
    offerToReceiveAudio: true,
  }
  pc.createOffer(createOfferParams).then(
    (offer) => {
      pc.setLocalDescription(offer).then(noop, () => {
        test.reportFatal('Failed to set specified offer')
      })
    },
    () => {
      test.reportFatal('Failed to create specified offer')
    },
  )

  // Timout
  const testDurationMs = 10000
  setTimeoutWithProgressBar(
    test,
    testDurationMs,
    test.reportFatal.bind(test, 'gatherCandidates: Timed out'),
  )
}

//
//
//

export class Call {
  static noFilter() {
    return true
  }

  static isRelay(candidate: RTCIceCandidate) {
    return candidate.type === 'relay'
  }

  static isNotHostCandidate(candidate: RTCIceCandidate) {
    return candidate.type !== 'host'
  }

  static isReflexive(candidate: RTCIceCandidate) {
    return candidate.type === 'srflx'
  }

  static isHost(candidate: RTCIceCandidate) {
    return candidate.type === 'host'
  }

  static isUdp(candidate: RTCIceCandidate) {
    return candidate.protocol === 'udp'
  }

  static isTcp(candidate: RTCIceCandidate) {
    return candidate.protocol === 'tcp'
  }

  static isReflexiveOrHost(candidate: RTCIceCandidate) {
    return Call.isReflexive(candidate) || Call.isHost(candidate)
  }

  static isIpv6(candidate: RTCIceCandidate) {
    return candidate.address?.indexOf(':') !== -1
  }

  // Parse a 'candidate:' line into a JSON object.
  static parseCandidate(text: string) {
    let ice = new RTCIceCandidate({
      candidate: text,
      sdpMid: '',
      sdpMLineIndex: 0,
    })

    // SHAM RTCIceCandidate on FF ?
    if (!ice.type) {
      const candidateStr = 'candidate:'
      const pos = text.indexOf(candidateStr) + candidateStr.length
      const fields = text.toLocaleLowerCase().substr(pos).split(' ')
      //console.log('parseCandidate', text)
      ice = {
        type: fields[7],
        protocol: fields[2],
        address: fields[4],
        port: fields[5],
      } as any
    }

    return ice
  }

  // Get a TURN config, either from settings or from CEOD.
  static asyncCreateTurnConfig(
    test: Test,
    onSuccess: (config: any) => void,
    onError: (err: Error) => void,
  ) {
    const settings = test.settings

    if (
      settings.iceCandidates &&
      typeof settings.iceCandidates.turnURI === 'string' &&
      settings.iceCandidates.turnURI !== ''
    ) {
      const iceServer = {
        username: settings.iceCandidates.turnUsername || '',
        credential: settings.iceCandidates.turnCredential || '',
        urls: settings.iceCandidates.turnURI.split(','),
      }

      const config = {
        iceServers: [iceServer],
      }

      test.traceEventInstant('turn-config', config)
      setTimeout(onSuccess.bind(null, config), 0)
    } else {
      onError(new Error('No turnURI provided'))
    }
  }

  // Get a STUN config, either from settings or from CEOD.
  static asyncCreateStunConfig(
    test: Test,
    onSuccess: (config: any) => void,
    onError: (err: Error) => void,
  ) {
    const settings = test.settings
    if (
      settings.iceCandidates &&
      typeof settings.iceCandidates.stunURI === 'string' &&
      settings.iceCandidates.stunURI !== ''
    ) {
      const iceServer = {
        urls: settings.iceCandidates.stunURI.split(','),
      }
      const config = {
        iceServers: [iceServer],
      }
      test.traceEventInstant('stun-config', config)
      setTimeout(onSuccess.bind(null, config), 0)
    } else {
      onError(new Error('No stunURI provided'))
    }
  }

  static openPeerConnection(
    config: RTCConfiguration,
    params?: any,
  ): RTCPeerConnection {
    if (window.RTCPeerConnection) {
      // @ts-ignore params cause still works
      return new RTCPeerConnection(config, params)
    } else {
      throw new Error('RTCPeerConnection is not supported.')
    }
  }

  static closePeerConnection(pc: RTCPeerConnection) {
    try {
      pc.close()
    } catch (err) {
      // Uncaught InvalidStateError: Failed to execute 'close' on 'RTCPeerConnection': The RTCPeerConnection's signalingState is 'closed'.
    } finally {
      pc.dispatchEvent(new CustomEvent('close'))
    }
  }

  //
  //
  //

  test: Test
  pc1: RTCPeerConnection
  pc2: RTCPeerConnection

  traceEvent: any
  iceCandidateFilter_: (candidate: RTCIceCandidate) => boolean
  constrainVideoBitrateKbps_: any
  constrainOfferToRemoveVideoFec_: any

  constructor(test: Test, config?: any) {
    this.test = test
    this.test.call = this
    this.traceEvent = test.traceEventAsync('call')
    this.traceEvent({ state: 'config', config })

    this.pc1 = Call.openPeerConnection(config)
    this.pc2 = Call.openPeerConnection(config)

    this.pc1.addEventListener(
      'icecandidate',
      this.onIceCandidate.bind(this, this.pc2),
    )

    this.pc2.addEventListener(
      'icecandidate',
      this.onIceCandidate.bind(this, this.pc1),
    )

    this.iceCandidateFilter_ = Call.noFilter
  }

  establishConnection() {
    this.traceEvent({ state: 'start' })
    this.pc1.createOffer().then((offer: RTCSessionDescriptionInit) => {
      this.gotOffer(offer as RTCSessionDescription)
    }, this.test.reportFatal)
  }

  close() {
    this.traceEvent({ state: 'end' })

    Call.closePeerConnection(this.pc1)
    Call.closePeerConnection(this.pc2)
  }

  reportFatal(err: Error) {
    this.test.reportFatal(err)
  }

  setIceCandidateFilter(filter: (candidate: RTCIceCandidate) => boolean) {
    this.iceCandidateFilter_ = filter
  }

  // Constraint max video bitrate by modifying the SDP when creating an answer.
  constrainVideoBitrate(maxVideoBitrateKbps: number) {
    this.constrainVideoBitrateKbps_ = maxVideoBitrateKbps
  }

  // Remove video FEC if available on the offer.
  disableVideoFec() {
    this.constrainOfferToRemoveVideoFec_ = true
  }
  // When the peerConnection is closed the statsCb is called once with an array
  // of gathered stats.
  gatherStats(
    peerConnection: RTCPeerConnection,
    statsCb: (
      statsReports: RTCStatsReportEntry[],
      statsCollectTime: number[],
    ) => void,
    interval: number,
  ) {
    const statsReports: RTCStatsReportEntry[] = []
    const statsCollectTime: number[] = []

    const closeListener = (event: any) => {
      peerConnection.removeEventListener('close', closeListener)
      statsCb(statsReports, statsCollectTime)
    }

    peerConnection.addEventListener('close', closeListener)

    const getStats = () => {
      peerConnection.getStats().then(gotStats)
    }

    const gotStats = (stats: RTCStatsReport) => {
      stats.forEach((report: RTCStatsReportEntry) => {
        statsReports.push(report)
        statsCollectTime.push(report.timestamp)
      })
      getStats()
    }

    getStats()
  }

  gotOffer(offer: RTCSessionDescription) {
    if (this.constrainOfferToRemoveVideoFec_) {
      offer = replaceSdp(
        offer,
        offer.sdp.replace(/(m=video 1 [^\r]+)(116 117)(\r\n)/g, '$1\r\n'),
      )
      offer = replaceSdp(
        offer,
        offer.sdp.replace(/a=rtpmap:116 red\/90000\r\n/g, ''),
      )
      offer = replaceSdp(
        offer,
        offer.sdp.replace(/a=rtpmap:117 ulpfec\/90000\r\n/g, ''),
      )
    }
    this.pc1.setLocalDescription(offer).then(() => {
      this.pc2.setRemoteDescription(offer).then(() => {
        this.pc2.createAnswer().then((answer) => {
          this.gotAnswer(answer as RTCSessionDescription)
        }, this.reportFatal.bind(this))
      }, this.reportFatal.bind(this))
    }, this.reportFatal.bind(this))
  }

  gotAnswer(answer: RTCSessionDescription) {
    if (this.constrainVideoBitrateKbps_) {
      answer = replaceSdp(
        answer,
        answer.sdp.replace(
          /a=mid:video\r\n/g,
          'a=mid:video\r\nb=AS:' + this.constrainVideoBitrateKbps_ + '\r\n',
        ),
      )
    }

    this.pc2.setLocalDescription(answer).then(() => {
      this.pc1
        .setRemoteDescription(answer)
        .then(noop, this.reportFatal.bind(this))
    }, this.reportFatal.bind(this))
  }

  onIceCandidate(
    otherPeer: RTCPeerConnection,
    event: RTCPeerConnectionIceEvent,
  ) {
    // Check for legacy event.candidate on FF
    if (event.candidate?.candidate) {
      const parsed = Call.parseCandidate(event.candidate.candidate)
      if (this.iceCandidateFilter_(parsed)) {
        this.test.reportInfo(
          'Add IceCandidate: ' +
            parsed.type +
            '/' +
            parsed.protocol +
            ' ' +
            parsed.address +
            ':' +
            parsed.port,
        )
        otherPeer.addIceCandidate(event.candidate)
      } else if (parsed) {
        this.test.reportInfo(
          'Skip IceCandidate: ' +
            parsed.type +
            '/' +
            parsed.protocol +
            ' ' +
            parsed.address +
            ':' +
            parsed.port,
        )
      }
    }
  }
}
